import { useCallback, useEffect, useState } from 'react';
import RestaurantSettingsService from './RestaurantSettingsService';
import {
  RestaurantSettingsAlert,
  RestaurantSettingsDTOMap,
  RestaurantSettingsSectionKey,
  RestaurantSettingsUpdateDTOMap,
} from './RestaurantSettingsTypes';

const getErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.message ||
  'No fue posible completar la operación. Inténtelo nuevamente.';

const getErrorTitle = (error: any) =>
  error?.response?.data?.title || 'Error de configuración';

const useRestaurantSettingsForm = <
  K extends RestaurantSettingsSectionKey,
  F extends RestaurantSettingsUpdateDTOMap[K]
>(
  branchId: number,
  section: K,
  fromDTO: (dto: RestaurantSettingsDTOMap[K]) => F,
  toDTO: (form: F) => RestaurantSettingsUpdateDTOMap[K]
) => {
  const [form, setForm] = useState<F | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<RestaurantSettingsAlert | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setAlert(null);
    try {
      const response = await RestaurantSettingsService.getByBranch(branchId);
      const sectionData = response.data?.[section];
      if (!sectionData) {
        throw new Error('La sección solicitada no está disponible para esta sucursal.');
      }
      setForm(fromDTO(sectionData));
    } catch (error) {
      setAlert({
        type: 'error',
        title: getErrorTitle(error),
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, [branchId, section, fromDTO]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = <P extends keyof F>(field: P, value: F[P]) => {
    setForm((current) =>
      current ? ({ ...current, [field]: value } as F) : current
    );
  };

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    setAlert(null);
    try {
      const response = await RestaurantSettingsService.updateSection(
        branchId,
        section,
        toDTO(form)
      );
      setForm(fromDTO(response.data));
      setAlert({
        type: 'success',
        title: response.title || 'Configuración guardada',
        message: response.message || 'Los cambios se guardaron correctamente.',
      });
    } catch (error) {
      // El formulario no se reinicia: los cambios quedan disponibles para reintentar.
      setAlert({
        type: 'error',
        title: getErrorTitle(error),
        message: getErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    loading,
    saving,
    alert,
    setAlert,
    updateField,
    save,
    reload: load,
  };
};

export default useRestaurantSettingsForm;

